import { getAdminCauldronByPath } from '@/admin/config/cauldrons';
import { watch } from 'vue';
import { createRouter, createWebHashHistory } from 'vue-router';

import i18n from '@/i18n';

/**
 * Admin portal router. Hash history to match the customer SPA (the axios
 * clients and session-expiry redirects assume `#/login`).
 *
 * Pages for text / users / notifications are Phase 1–3 deliverables; Phase 0
 * ships the shell, the access gate and placeholders so the surface is
 * deployable and navigable on the acceptance subdomain.
 *
 * `meta.titleKey` (not `meta.title`) holds an i18n key, matching the customer
 * router — the document title must follow the active locale.
 */
const routes = [
  {
    path: '/login',
    name: 'admin-login',
    component: () => import('../pages/AdminLogin.vue'),
    meta: { public: true, blank: true, titleKey: 'admin.route_titles.login' },
  },
  {
    path: '/access-denied',
    name: 'admin-access-denied',
    component: () => import('../pages/AccessDenied.vue'),
    meta: { public: true, blank: true, titleKey: 'admin.route_titles.access_denied' },
  },
  {
    path: '/',
    name: 'admin-home',
    component: () => import('../pages/AdminHome.vue'),
    meta: { titleKey: 'admin.route_titles.home' },
  },
  {
    // Reached from the homepage TODO inbox; no sidebar item (it belongs to the home page).
    path: '/todos/settings',
    name: 'admin-todo-settings',
    component: () => import('../pages/TodoRouting.vue'),
    meta: { titleKey: 'admin.todos.settings.title' },
  },
  {
    path: '/forward-curve-evaluation/:market(day-ahead|intraday|ida1|ida2|ida3|fcr|afrr-up|afrr-down|afrr-up-energy|afrr-down-energy)?',
    name: 'admin-forward-curve-evaluation',
    component: () => import('@/admin/pages/forecasts/ForwardCurveEvaluation.vue'),
    meta: { titleKey: 'admin.forward_curve_evaluation.title' },
  },
  {
    path: '/forecast-evaluation/:forecaster(day-ahead|spikes|fcr|afrr|mfrr|rs2)?',
    name: 'admin-forecast-evaluation',
    component: () => import('../pages/forecasts/ForecastEvaluation.vue'),
    meta: { titleKey: 'admin.forecast_evaluation.title' },
  },
  // Informational dashboards. These only READ — every action they surface routes
  // into the existing management pages, so they carry no write paths of their own.
  {
    path: '/data-health',
    name: 'admin-data-health',
    component: () => import('../pages/dashboards/DataHealthDashboard.vue'),
    meta: { titleKey: 'admin.route_titles.data_health' },
  },
  {
    path: '/activity',
    name: 'admin-activity',
    component: () => import('../pages/dashboards/ActivityDashboard.vue'),
    meta: { titleKey: 'admin.route_titles.activity' },
  },
  {
    path: '/mcp',
    name: 'admin-mcp',
    component: () => import('../pages/dashboards/McpDashboard.vue'),
    meta: { titleKey: 'admin.route_titles.mcp' },
  },
  {
    path: '/text',
    name: 'admin-text',
    component: () => import('../pages/text/TextManagement.vue'),
    meta: { titleKey: 'admin.route_titles.text' },
  },
  {
    path: '/text/:locale',
    name: 'admin-text-locale',
    component: () => import('../pages/text/TextLocaleEditor.vue'),
    meta: { titleKey: 'admin.route_titles.text_locale' },
  },
  {
    path: '/text/datasets/:datasetId',
    name: 'admin-text-dataset',
    component: () => import('../pages/text/TextEntityEditor.vue'),
    meta: {
      titleKey: 'admin.route_titles.text_dataset',
      entityType: 'dataset',
    },
  },
  {
    path: '/text/visualizations/:visualizationId',
    name: 'admin-text-visualization',
    component: () => import('../pages/text/TextEntityEditor.vue'),
    meta: {
      titleKey: 'admin.route_titles.text_visualization',
      entityType: 'visualization',
    },
  },
  {
    path: '/text/streams/:streamId(.*)',
    name: 'admin-text-stream',
    component: () => import('../pages/text/TextEntityEditor.vue'),
    meta: { titleKey: 'admin.route_titles.text_stream', entityType: 'stream' },
  },
  {
    path: '/datasets',
    name: 'admin-datasets',
    component: () => import('../pages/datasets/DatasetManagement.vue'),
    meta: { titleKey: 'admin.route_titles.datasets' },
  },
  {
    path: '/datasets/new',
    name: 'admin-dataset-new',
    component: () => import('../pages/datasets/DatasetEdit.vue'),
    meta: { titleKey: 'admin.route_titles.dataset_new' },
  },
  {
    path: '/datasets/:datasetId/edit',
    name: 'admin-dataset-edit',
    component: () => import('../pages/datasets/DatasetEdit.vue'),
    meta: { titleKey: 'admin.route_titles.dataset_edit' },
  },
  {
    path: '/datasets/:datasetId/preview',
    name: 'admin-dataset-preview',
    component: () => import('../pages/config-editor/pages/ConfigPreviewPage.vue'),
    props: { entityType: 'dataset' },
    meta: { titleKey: 'admin.route_titles.dataset_preview' },
  },
  {
    path: '/datasets/:datasetId',
    name: 'admin-dataset-detail',
    component: () => import('../pages/datasets/DatasetDetail.vue'),
    meta: { titleKey: 'admin.route_titles.dataset_detail' },
  },
  {
    path: '/visualizations',
    name: 'admin-visualizations',
    component: () => import('../pages/visualizations/VisualizationManagement.vue'),
    meta: { titleKey: 'admin.route_titles.visualizations' },
  },
  {
    path: '/visualizations/new',
    name: 'admin-visualization-new',
    component: () => import('../pages/visualizations/VisualizationEdit.vue'),
    meta: { titleKey: 'admin.route_titles.visualization_new' },
  },
  {
    path: '/visualizations/:visualizationId/edit',
    name: 'admin-visualization-edit',
    component: () => import('../pages/visualizations/VisualizationEdit.vue'),
    meta: { titleKey: 'admin.route_titles.visualization_edit' },
  },
  {
    path: '/visualizations/:visualizationId/preview',
    name: 'admin-visualization-preview',
    component: () => import('../pages/config-editor/pages/ConfigPreviewPage.vue'),
    props: { entityType: 'visualization' },
    meta: { titleKey: 'admin.route_titles.visualization_preview' },
  },
  {
    path: '/visualizations/:visualizationId(.*)',
    name: 'admin-visualization-detail',
    component: () => import('../pages/visualizations/VisualizationDetail.vue'),
    meta: { titleKey: 'admin.route_titles.visualization_detail' },
  },
  {
    path: '/config-editor',
    name: 'admin-config-editor',
    component: () => import('../pages/config-editor/ConfigEditor.vue'),
    meta: { titleKey: 'admin.route_titles.config_editor' },
  },
  {
    path: '/knowledge-graph',
    name: 'admin-knowledge-graph',
    component: () => import('../pages/graph/KnowledgeGraphDashboard.vue'),
    meta: { titleKey: 'admin.route_titles.knowledge_graph' },
  },
  {
    // `/entities` and `/facts` were separate READ-ONLY renderings of rows that
    // `/graph-nodes` already lists and can edit. Three pages over one store, with no signpost
    // saying which one lets you change anything. They redirect into the registry with the kind
    // preselected — kept as redirects rather than deleted because they are linked from detail
    // breadcrumbs and from anybody's bookmarks.
    path: '/entities',
    name: 'admin-entities',
    redirect: { name: 'admin-graph-nodes', query: { kind: 'entity' } },
  },
  {
    path: '/entities/:entityId(.*)',
    name: 'admin-entity-detail',
    component: () => import('../pages/graph/EntityDetail.vue'),
    meta: { titleKey: 'admin.route_titles.entity_detail' },
  },
  {
    path: '/entity-proposals',
    name: 'admin-entity-proposals',
    component: () => import('../pages/graph/EntityProposalReview.vue'),
    meta: { titleKey: 'admin.route_titles.entity_proposals' },
  },
  {
    path: '/newsroom/overview',
    name: 'admin-newsroom-overview',
    component: () => import('../pages/newsroom/NewsroomOverview.vue'),
    meta: { titleKey: 'admin.newsroom.overview.title' },
  },
  {
    path: '/newsroom/share-links',
    name: 'admin-newsroom-share-links',
    component: () => import('../pages/newsroom/NewsroomShareLinks.vue'),
    meta: { titleKey: 'admin.newsroom.share_links.title' },
  },
  {
    path: '/newsroom/planning',
    name: 'admin-newsroom-planning',
    component: () => import('../pages/newsroom/NewsroomPlanning.vue'),
    meta: { titleKey: 'admin.newsroom.planning.title' },
  },
  {
    // The newsroom desk: start an edition and watch it being written. `/newsroom/runs/:runId`
    // is the same page with a run selected, so a run link can be shared and replayed.
    path: '/newsroom',
    name: 'admin-newsroom',
    component: () => import('../pages/newsroom/NewsroomDesk.vue'),
    meta: { titleKey: 'admin.route_titles.newsroom' },
  },
  {
    path: '/newsroom/runs/:runId',
    name: 'admin-newsroom-run',
    component: () => import('../pages/newsroom/NewsroomDesk.vue'),
    meta: { titleKey: 'admin.route_titles.newsroom' },
  },
  {
    path: '/newsroom/review',
    name: 'admin-newsroom-review',
    redirect: (to) => ({ name: 'admin-newsroom-overview', query: to.query }),
  },
  {
    path: '/newsroom/read/:articleId(.*)',
    name: 'admin-newsroom-read',
    component: () => import('../pages/newsroom/NewsroomArticleRead.vue'),
    meta: { titleKey: 'admin.newsroom.reader.read' },
  },
  {
    // Article ids can include slashes; review keeps passage comments and revision history.
    path: '/newsroom/articles/:articleId(.*)',
    name: 'admin-newsroom-article',
    component: () => import('../pages/newsroom/NewsroomArticleReview.vue'),
    meta: { titleKey: 'admin.route_titles.newsroom_article' },
  },
  {
    path: '/graph-nodes',
    name: 'admin-graph-nodes',
    component: () => import('../pages/graph/NodeManagement.vue'),
    meta: { titleKey: 'admin.route_titles.graph_nodes' },
  },
  {
    // `:kind/:nodeId(.*)` — the kind is a closed vocabulary, the id is not: fact ids are
    // kebab-case today but a route that only matches a strict segment breaks the day one
    // carries a slash.
    path: '/graph-nodes/:kind/:nodeId(.*)',
    name: 'admin-graph-node-detail',
    component: () => import('../pages/graph/NodeDetail.vue'),
    meta: { titleKey: 'admin.route_titles.graph_node_detail' },
  },
  {
    path: '/facts',
    name: 'admin-facts',
    redirect: { name: 'admin-graph-nodes', query: { kind: 'fact' } },
  },
  {
    // `(.*)` like the entity and event routes: fact ids are kebab-case and safe today,
    // but a route that only matches a strict segment breaks the day one carries a slash.
    path: '/facts/:factId(.*)',
    name: 'admin-fact-detail',
    component: () => import('../pages/graph/FactDetail.vue'),
    meta: { titleKey: 'admin.route_titles.fact_detail' },
  },
  {
    path: '/articles',
    name: 'admin-articles',
    component: () => import('../pages/graph/ArticleManagement.vue'),
    meta: { titleKey: 'admin.route_titles.articles' },
  },
  {
    path: '/articles/:articleId(.*)',
    name: 'admin-article-detail',
    component: () => import('../pages/graph/ArticleDetail.vue'),
    meta: { titleKey: 'admin.route_titles.article_detail' },
  },
  {
    path: '/events',
    name: 'admin-events',
    component: () => import('../pages/graph/EventManagement.vue'),
    meta: { titleKey: 'admin.route_titles.events' },
  },
  {
    path: '/events/:eventId(.*)',
    name: 'admin-event-detail',
    component: () => import('../pages/graph/EventDetail.vue'),
    meta: { titleKey: 'admin.route_titles.event_detail' },
  },
  {
    path: '/graph-runs',
    name: 'admin-graph-runs',
    component: () => import('../pages/graph/GraphRuns.vue'),
    meta: { titleKey: 'admin.route_titles.graph_runs' },
  },
  {
    // The run list and one run share a page: a detail route that re-fetches the list to show
    // one row is two screens maintaining one idea.
    path: '/graph-runs/:runId(.*)',
    name: 'admin-graph-run-detail',
    component: () => import('../pages/graph/GraphRuns.vue'),
    meta: { titleKey: 'admin.route_titles.graph_runs' },
  },
  {
    path: '/graph-review',
    name: 'admin-graph-review',
    component: () => import('../pages/graph/GraphReview.vue'),
    meta: { titleKey: 'admin.route_titles.graph_review' },
  },
  {
    path: '/graph-changelog',
    name: 'admin-graph-changelog',
    component: () => import('../pages/graph/GraphChangelog.vue'),
    meta: { titleKey: 'admin.route_titles.graph_changelog' },
  },
  {
    path: '/streams',
    name: 'admin-streams',
    component: () => import('../pages/streams/StreamManagement.vue'),
    meta: { titleKey: 'admin.route_titles.streams' },
  },
  {
    path: '/alert-targets',
    name: 'admin-alert-targets',
    component: () => import('../pages/alerts/AlertTargetRegistry.vue'),
    meta: { titleKey: 'admin.route_titles.alert_targets' },
  },
  {
    path: '/streams/:streamId(.*)',
    name: 'admin-stream-detail',
    component: () => import('../pages/streams/StreamDetail.vue'),
    meta: { titleKey: 'admin.route_titles.stream_detail' },
  },
  {
    path: '/users',
    name: 'admin-users',
    component: () => import('../pages/users/UserManagement.vue'),
    meta: { titleKey: 'admin.route_titles.users' },
  },
  {
    // Only resumes an unfinished onboarding request; new invitations use the wizard.
    path: '/users/invite',
    name: 'admin-users-invite',
    component: () => import('../pages/users/UserInvite.vue'),
    beforeEnter: (to) =>
      typeof to.query.onboarding === 'string' || { name: 'admin-invitations-new', query: to.query },
    meta: { titleKey: 'admin.route_titles.users_invite' },
  },
  {
    path: '/users/:slug',
    name: 'admin-user-detail',
    component: () => import('../pages/users/UserDetail.vue'),
    meta: { titleKey: 'admin.route_titles.user_detail' },
  },
  {
    path: '/invitations',
    name: 'admin-invitations',
    component: () => import('../pages/users/Invitations.vue'),
    meta: { titleKey: 'admin.invitations.title' },
  },
  {
    path: '/invitations/new',
    name: 'admin-invitations-new',
    component: () => import('../pages/users/InvitationWizard.vue'),
    meta: { titleKey: 'admin.invite_wizard.title' },
  },
  {
    // New API-only customer: organisation, invoiced dataset subscription and invitations.
    path: '/invitations/new/api',
    name: 'admin-api-customer-new',
    component: () => import('../pages/users/ApiCustomerWizard.vue'),
    meta: { titleKey: 'admin.api_customer.title' },
  },
  {
    path: '/tenants',
    name: 'admin-tenants',
    component: () => import('../pages/tenants/TenantManagement.vue'),
    meta: { titleKey: 'admin.route_titles.tenants' },
  },
  {
    path: '/tenants/new',
    name: 'admin-tenants-new',
    // Organisations are created inside the invite flow, so they never exist without an offer.
    redirect: (to) => ({ name: 'admin-invitations-new', query: to.query }),
    meta: { titleKey: 'admin.route_titles.tenants_new' },
  },
  {
    // Must be declared BEFORE the `/tenants/:tenantId` shell, or "new-trial"
    // would be matched as a tenant id.
    path: '/tenants/new-feedback',
    name: 'admin-feedback-account-new',
    redirect: { name: 'admin-invitations-new', query: { offer: 'feedback' } },
  },
  {
    path: '/tenants/new-trial',
    name: 'admin-trial-account-new',
    redirect: { name: 'admin-invitations-new', query: { offer: 'trial' } },
  },
  {
    // One tenant console. TenantShell owns the single tenant read plus the
    // always-visible commercial summary; the tabs below are its children, so the
    // child paths (and therefore bookmarks and QA selectors) are unchanged.
    path: '/tenants/:tenantId',
    component: () => import('../pages/tenants/TenantShell.vue'),
    children: [
      {
        path: '',
        name: 'admin-tenant-detail',
        component: () => import('../pages/tenants/TenantOverview.vue'),
        meta: { titleKey: 'admin.route_titles.tenant_detail' },
      },
      { path: 'tokens/transactions', name: 'admin-tenant-wallet-transactions', component: () => import('../pages/tenants/TenantTokens.vue'), meta: { titleKey: 'admin.functions.transactions', walletView: 'transactions' } },
      { path: 'tokens/reservations', name: 'admin-tenant-wallet-reservations', component: () => import('../pages/tenants/TenantTokens.vue'), meta: { titleKey: 'admin.functions.reservations', walletView: 'reservations' } },
      { path: 'tokens/history', name: 'admin-tenant-wallet-history', component: () => import('../pages/tenants/TenantTokens.vue'), meta: { titleKey: 'admin.functions.history', walletView: 'history' } },
      { path: 'tokens/settings', name: 'admin-tenant-wallet-settings', component: () => import('../pages/tenants/TenantTokens.vue'), meta: { titleKey: 'admin.functions.settings', walletView: 'settings' } },
      { path: 'tokens/recharge', name: 'admin-tenant-wallet-recharge', component: () => import('../pages/tenants/TenantTokens.vue'), meta: { titleKey: 'admin.functions.settings', walletView: 'recharge' } },
      { path: 'entitlements/api', name: 'admin-tenant-api-access', component: () => import('../pages/tenants/TenantAccess.vue'), meta: { titleKey: 'admin.tenant_api_access.title' } },
      { path: 'entitlements/api/new', name: 'admin-tenant-api-wizard', component: () => import('../pages/tenants/TenantAccess.vue'), meta: { titleKey: 'admin.tenant_api_access.title' } },
      { path: 'subscription/trial', name: 'admin-tenant-trial', component: () => import('../pages/tenants/TenantSubscription.vue'), meta: { titleKey: 'admin.tenant_subscription.start_title' } },
      { path: 'subscription/plan-grant', name: 'admin-tenant-plan-grant', component: () => import('../pages/tenants/TenantSubscription.vue'), meta: { titleKey: 'admin.plan_grant.title' } },
      { path: 'subscription/feedback-offer', name: 'admin-tenant-feedback-offer', component: () => import('../pages/tenants/TenantSubscription.vue'), meta: { titleKey: 'admin.feedback_offer.title' } },
      { path: 'activity', name: 'admin-tenant-activity', component: () => import('../pages/tenants/TenantActivity.vue'), meta: { titleKey: 'admin.functions.activity' } },
      { path: 'members', name: 'admin-tenant-members', component: () => import('../pages/tenants/TenantMembers.vue'), meta: { titleKey: 'admin.functions.members' } },
      {
        path: 'subscription',
        name: 'admin-tenant-subscription',
        component: () => import('../pages/tenants/TenantSubscription.vue'),
        meta: { titleKey: 'admin.route_titles.tenant_subscription' },
      },
      {
        path: 'tokens',
        name: 'admin-tenant-tokens',
        component: () => import('../pages/tenants/TenantTokens.vue'),
        meta: { titleKey: 'admin.route_titles.tenant_tokens' },
      },
      {
        // Entitlements AND quotas AND API keys: one "needs more access" job.
        // Path kept as `entitlements` so existing links still resolve.
        path: 'entitlements',
        name: 'admin-tenant-entitlements',
        component: () => import('../pages/tenants/TenantAccess.vue'),
        meta: { titleKey: 'admin.route_titles.tenant_access' },
      },
      // The standalone quotas page merged into Access.
      { path: 'quotas', redirect: { name: 'admin-tenant-entitlements' } },
    ],
  },
  {
    path: '/billing/plans',
    name: 'admin-plans',
    component: () => import('../pages/billing/Plans.vue'),
    meta: { titleKey: 'admin.route_titles.plans' },
  },
  {
    path: '/billing/packages',
    name: 'admin-customer-packages',
    component: () => import('../pages/billing/CustomerPackages.vue'),
    meta: { titleKey: 'admin.customer_package.title' },
  },
  {
    path: '/billing/promotion-offers',
    name: 'admin-promotion-offers',
    component: () => import('../pages/billing/PromotionOffers.vue'),
    meta: { titleKey: 'admin.promotion_offer.title' },
  },
  {
    path: '/billing/promotions',
    name: 'admin-promotion-campaigns',
    component: () => import('../pages/billing/PromotionCampaigns.vue'),
    meta: { titleKey: 'admin.promotion_campaign.title' },
  },
  {
    path: '/billing/discounts',
    name: 'admin-discounts',
    component: () => import('../pages/billing/Discounts.vue'),
    meta: { titleKey: 'admin.route_titles.discounts' },
  },
  {
    // Priced per dataset and sold separately from the plans, so it gets its own
    // page rather than a section inside the plan editor.
    path: '/billing/api-datasets',
    name: 'admin-api-datasets',
    component: () => import('../pages/billing/ApiDatasets.vue'),
    meta: { titleKey: 'admin.route_titles.api_datasets' },
  },
  {
    // Curated sets of API datasets sold as one product (PI-635).
    path: '/billing/bundles',
    name: 'admin-dataset-bundles',
    component: () => import('../pages/billing/DatasetBundles.vue'),
    meta: { titleKey: 'admin.route_titles.dataset_bundles' },
  },
  {
    path: '/billing/bundles/new',
    name: 'admin-dataset-bundle-new',
    component: () => import('../pages/billing/DatasetBundleEditor.vue'),
    meta: { titleKey: 'admin.route_titles.dataset_bundles' },
  },
  {
    path: '/billing/bundles/:slug',
    name: 'admin-dataset-bundle',
    component: () => import('../pages/billing/DatasetBundleDetail.vue'),
    meta: { titleKey: 'admin.route_titles.dataset_bundles' },
  },
  {
    path: '/billing/access-catalogue',
    name: 'admin-access-catalogue',
    component: () => import('../pages/billing/AccessCatalogue.vue'),
    meta: { titleKey: 'admin.access_catalogue.title' },
  },
  {
    path: '/billing/reference',
    name: 'admin-billing-reference',
    component: () => import('../pages/billing/BillingReference.vue'),
    meta: { titleKey: 'admin.route_titles.billing_reference' },
  },
  {
    // Moved out of Billing: revoking a key is access control, not commerce.
    path: '/api-keys',
    name: 'admin-api-keys',
    component: () => import('../pages/api-keys/AdminApiKeysPage.vue'),
    meta: { titleKey: 'admin.route_titles.api_keys' },
  },
  // Preserve billing bookmarks at their specific read-only reference views.
  { path: '/plans', redirect: { name: 'admin-plans' } },
  { path: '/token-rates', redirect: { name: 'admin-token-rates' } },
  { path: '/plan-quotas', redirect: { name: 'admin-plan-quota-defaults' } },
  { path: '/stripe', redirect: { name: 'admin-stripe-catalogue' } },
  { path: '/stripe/products/:productId', redirect: { name: 'admin-plans' } },
  {
    path: '/notification-templates',
    name: 'admin-notification-templates',
    component: () => import('../pages/notifications/NotificationTemplates.vue'),
    meta: { titleKey: 'admin.workspace_items.notification_templates' },
  },
  {
    path: '/notifications',
    name: 'admin-notifications',
    component: () => import('../pages/notifications/NotificationManagement.vue'),
    meta: { titleKey: 'admin.route_titles.notifications' },
  },
  {
    path: '/notifications/sent',
    name: 'admin-notifications-sent',
    component: () => import('../pages/notifications/SentNotifications.vue'),
    meta: { titleKey: 'admin.route_titles.notifications_sent' },
  },
  {
    // Contact, sales and API-access requests; the `requests` TODOs link here with `?id=`.
    path: '/requests',
    name: 'admin-requests',
    component: () => import('../pages/requests/InboundRequests.vue'),
    meta: { titleKey: 'admin.requests.title' },
  },
  {
    // SEO/GEO briefs from the weekly worker run; the `seo.opportunity` TODO links here with `?id=`.
    path: '/seo-opportunities',
    name: 'admin-seo-opportunities',
    component: () => import('../pages/seo/SeoOpportunities.vue'),
    meta: { titleKey: 'admin.seo_opportunities.title' },
  },
  {
    path: '/feedback',
    name: 'admin-feedback',
    component: () => import('../pages/feedback/FeedbackReports.vue'),
    meta: { titleKey: 'admin.route_titles.feedback' },
  },
  {
    path: '/feedback/:reportId',
    name: 'admin-feedback-detail',
    component: () => import('../pages/feedback/FeedbackReportDetail.vue'),
    meta: { titleKey: 'admin.route_titles.feedback_detail' },
  },
  {
    path: '/tools/alerts',
    name: 'admin-alerts',
    component: () => import('../pages/alerts/AlertsList.vue'),
    meta: { titleKey: 'admin.route_titles.alerts' },
  },
  {
    path: '/tools/alerts/:alertId',
    name: 'admin-alert-detail',
    component: () => import('../pages/alerts/AlertDetail.vue'),
    meta: { titleKey: 'admin.route_titles.alert_detail' },
  },
  {
    path: '/tools/runs',
    name: 'admin-tool-runs',
    component: () => import('../pages/tools/ToolRunsList.vue'),
    meta: { titleKey: 'admin.route_titles.tool_runs' },
  },
  {
    path: '/tools/runs/:runId',
    name: 'admin-tool-run-detail',
    component: () => import('../pages/tools/ToolRunDetail.vue'),
    meta: { titleKey: 'admin.route_titles.tool_run_detail' },
  },
  {
    path: '/tools/battery-configs',
    name: 'admin-battery-configs',
    component: () => import('../pages/tools/BatteryConfigsList.vue'),
    meta: { titleKey: 'admin.route_titles.battery_configs' },
  },
  {
    path: '/tools/battery-configs/:configId',
    name: 'admin-battery-config-detail',
    component: () => import('../pages/tools/BatteryConfigDetail.vue'),
    meta: { titleKey: 'admin.route_titles.battery_config_detail' },
  },
  {
    path: '/tools/congestion-contracts',
    name: 'admin-congestion-contracts',
    component: () => import('../pages/tools/CongestionContractsList.vue'),
    meta: { titleKey: 'admin.route_titles.congestion_contracts' },
  },
  {
    path: '/tools/congestion-contracts/:contractId',
    name: 'admin-congestion-contract-detail',
    component: () => import('../pages/tools/CongestionContractDetail.vue'),
    meta: { titleKey: 'admin.route_titles.congestion_contract_detail' },
  },
  {
    path: '/custom-assets/dashboards',
    name: 'admin-custom-dashboards',
    component: () => import('../pages/custom-assets/CustomAssets.vue'),
    meta: {
      titleKey: 'admin.route_titles.custom_dashboards',
      kind: 'dashboard',
    },
  },
  {
    path: '/custom-assets/kpis',
    name: 'admin-custom-kpis',
    component: () => import('../pages/custom-assets/CustomAssets.vue'),
    meta: { titleKey: 'admin.route_titles.custom_kpis', kind: 'kpi' },
  },
  { path: '/users/:slug/:userTab(profile|access|activity|content)', name: 'admin-user-view', component: () => import('../pages/users/UserDetail.vue'), meta: { titleKey: 'admin.route_titles.user_detail' } },
  { path: '/billing/api-datasets/requests', name: 'admin-quote-requests', component: () => import('../pages/billing/QuoteRequests.vue'), meta: { titleKey: 'admin.api_datasets.quotes_title' } },
  { path: '/billing/api-datasets/requests/:requestId', name: 'admin-quote-request', component: () => import('../pages/billing/QuoteRequests.vue'), meta: { titleKey: 'admin.api_datasets.quotes_title' } },
  { path: '/invitations/requests', name: 'admin-account-requests', component: () => import('../pages/users/Invitations.vue'), meta: { titleKey: 'admin.functions.account_requests' } },
  { path: '/notifications/sent/diagnostics', name: 'admin-notification-diagnostics', component: () => import('../pages/notifications/NotificationManagement.vue'), meta: { titleKey: 'admin.functions.diagnostics' } },
  { path: '/notifications/sent/diagnostics/probe', name: 'admin-notification-probe', component: () => import('../pages/notifications/NotificationManagement.vue'), meta: { titleKey: 'admin.notifications.probe_title' } },
  { path: '/billing/discounts/new', name: 'admin-discount-new', component: () => import('../pages/billing/Discounts.vue'), meta: { titleKey: 'admin.discount_builder.new_title' } },
  { path: '/billing/reference/token-rates', name: 'admin-token-rates', component: () => import('../pages/billing/BillingReference.vue'), meta: { titleKey: 'admin.billing_reference.rates_title', referenceView: 'rates' } },
  { path: '/billing/reference/plan-quotas', name: 'admin-plan-quota-defaults', component: () => import('../pages/billing/BillingReference.vue'), meta: { titleKey: 'admin.billing_reference.quotas_title', referenceView: 'quotas' } },
  { path: '/billing/reference/stripe', name: 'admin-stripe-catalogue', component: () => import('../pages/billing/BillingReference.vue'), meta: { titleKey: 'admin.billing_reference.catalog_title', referenceView: 'catalog' } },
  { path: '/data-health/billing', redirect: to => ({ name: 'admin-billing-health', query: to.query, hash: to.hash }) },
  { path: '/billing/reference/health', name: 'admin-billing-health', component: () => import('../pages/billing/BillingReference.vue'), meta: { titleKey: 'admin.functions.billing_health', referenceView: 'health' } },
  { path: '/newsroom/planning/groups', name: 'admin-newsroom-groups', component: () => import('../pages/newsroom/NewsroomSettings.vue'), meta: { titleKey: 'admin.functions.editorial_settings' } },
  { path: '/newsroom/planning/notes', name: 'admin-newsroom-notes', component: () => import('../pages/newsroom/NewsroomSettings.vue'), meta: { titleKey: 'admin.functions.editorial_settings' } },
  { path: '/newsroom/planning/groups/new', name: 'admin-newsroom-group-new', component: () => import('../pages/newsroom/NewsroomSettings.vue'), meta: { titleKey: 'admin.functions.editorial_settings' } },
  { path: '/newsroom/planning/groups/:focusId', name: 'admin-newsroom-group', component: () => import('../pages/newsroom/NewsroomSettings.vue'), meta: { titleKey: 'admin.functions.editorial_settings' } },
  { path: '/billing/promotions/new', name: 'admin-campaign-new', component: () => import('../pages/billing/PromotionCampaigns.vue'), meta: { titleKey: 'admin.promotion_campaign.new' } },
  { path: '/billing/promotions/:campaignKey/:campaignView(overview|recipients|deliveries)?', name: 'admin-campaign-detail', component: () => import('../pages/billing/PromotionCampaigns.vue'), meta: { titleKey: 'admin.promotion_campaign.title' } },
  { path: '/billing/promotions/:campaignKey/workflows/:workflow(edit|amend|add|send|close)', name: 'admin-campaign-workflow', component: () => import('../pages/billing/PromotionCampaigns.vue'), meta: { titleKey: 'admin.promotion_campaign.title' } },
  { path: '/billing/plans/:planKey/:planTab(overview|pricing|allowances|history)?', name: 'admin-plan-detail', component: () => import('../pages/billing/Plans.vue'), meta: { titleKey: 'admin.route_titles.plans' } },
  { path: '/text/publications', name: 'admin-text-publications', component: () => import('../pages/text/TextManagement.vue'), meta: { titleKey: 'admin.functions.publications' } },
  { path: '/text/publications/new', name: 'admin-text-publish', component: () => import('../pages/text/TextManagement.vue'), meta: { titleKey: 'admin.text.publication.title' } },
  { path: '/api-keys/:keyId', name: 'admin-api-key-detail', component: () => import('../pages/api-keys/AdminApiKeysPage.vue'), meta: { titleKey: 'admin.route_titles.api_keys' } },
  { path: '/notifications/sent/:notificationId', name: 'admin-notification-detail', component: () => import('../pages/notifications/SentNotifications.vue'), meta: { titleKey: 'admin.route_titles.notifications_sent' } },
  { path: '/billing/api-datasets/products/:datasetId', name: 'admin-api-product-detail', component: () => import('../pages/billing/ApiDatasets.vue'), meta: { titleKey: 'admin.route_titles.api_datasets' } },
  { path: '/billing/api-datasets/products/:datasetId/migrate', name: 'admin-api-product-migrate', component: () => import('../pages/billing/ApiDatasets.vue'), meta: { titleKey: 'admin.api_datasets.migrate_title' } },
  { path: '/billing/api-datasets/bulk/:bulkAction(adopt_suggestion|enable|disable)', name: 'admin-api-datasets-bulk', component: () => import('../pages/billing/ApiDatasets.vue'), meta: { titleKey: 'admin.route_titles.api_datasets' } },
  { path: '/billing/promotion-offers/new', name: 'admin-promotion-offer-new', component: () => import('../pages/billing/PromotionOffers.vue'), meta: { titleKey: 'admin.promotion_offer.create' } },
  { path: '/billing/promotion-offers/:offerKey/:offerView(overview|recipients)?', name: 'admin-promotion-offer-detail', component: () => import('../pages/billing/PromotionOffers.vue'), meta: { titleKey: 'admin.promotion_offer.title' } },
  { path: '/billing/bundles/:slug/edit', name: 'admin-dataset-bundle-edit', component: () => import('../pages/billing/DatasetBundleEditor.vue'), meta: { titleKey: 'admin.route_titles.dataset_bundles' } },
  { path: '/billing/discounts/:discountId', name: 'admin-discount-detail', component: () => import('../pages/billing/DiscountDetail.vue'), meta: { titleKey: 'admin.route_titles.discounts' } },
  { path: '/notification-templates/:templateKey', name: 'admin-notification-template-detail', component: () => import('../pages/notifications/NotificationTemplates.vue'), meta: { titleKey: 'admin.workspace_items.notification_templates' } },
  { path: '/custom-assets/dashboards/:assetId', name: 'admin-custom-dashboard-detail', component: () => import('../pages/custom-assets/CustomAssets.vue'), meta: { titleKey: 'admin.route_titles.custom_dashboards', kind: 'dashboard' } },
  { path: '/custom-assets/kpis/:assetId', name: 'admin-custom-kpi-detail', component: () => import('../pages/custom-assets/CustomAssets.vue'), meta: { titleKey: 'admin.route_titles.custom_kpis', kind: 'kpi' } },
  { path: '/feedback/access', name: 'admin-feedback-access', component: () => import('../pages/feedback/FeedbackAccess.vue'), meta: { titleKey: 'admin.functions.feedback_access' } },
  { path: '/:pathMatch(.*)*', redirect: { name: 'admin-home' } },
];

// Route ownership comes from the navigation registry, including nested tenant tabs.
function assignWorkspaces(records, parentPath = '') {
  for (const record of records) {
    const path = record.path.startsWith('/') ? record.path : `${parentPath}/${record.path}`;
    const workspace = getAdminCauldronByPath(path.replace(/\/$/, '') || '/');
    if (workspace && !record.meta?.public) {
      record.meta = { ...record.meta, cauldron: workspace.id };
    }
    if (record.children) assignWorkspaces(record.children, path);
  }
}
assignWorkspaces(routes);

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

function applyDocumentTitle(route) {
  const { t } = i18n.global;
  const base = t('admin.shell.brand');
  document.title = route?.meta?.titleKey ? `${t(route.meta.titleKey)} · ${base}` : base;
}

router.afterEach(applyDocumentTitle);

// Retitle on a locale change too, not only on navigation — otherwise switching
// language leaves the browser tab in the previous language until the next route.
watch(
  () => i18n.global.locale.value,
  () => applyDocumentTitle(router.currentRoute.value)
);

export default router;
